import { useState } from "react";
import { Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

const heroSlides = [
  {
    desktop: "/images/hero/slide1-desktop.png",
    mobile: "/images/hero/slide1-mobile.png",
  },
  {
    desktop: "/images/hero/slide2-desktop.png",
    mobile: "/images/hero/slide2-mobile.png",
    target: { type: "route", path: "#prabhupada" },
  },
  {
    desktop: "/images/hero/slide3-desktop.png",
    mobile: "/images/hero/slide3-mobile.png",
  },
  {
    desktop: "/images/hero/slide4-desktop.png",
    mobile: "/images/hero/slide4-mobile.png",
    target: { type: "route", path: "/donate" },
  },
  {
    desktop: "/images/hero/slide5-desktop.png",
    mobile: "/images/hero/slide5-mobile.png",
    target: { type: "route", path: "/donate" },
  },
  {
    desktop: "/images/hero/slide6-desktop.png",
    mobile: "/images/hero/slide6-mobile.png",
    target: {
      type: "external",
      url: "https://chat.whatsapp.com/BNq1EuvRYQC8Gfd9Je5yop?mode=gi_t",
    },
  }
];




const Home = () => {
  const [modalData, setModalData, setImpactModal, impactModal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [founderModal, setFounderModal] = useState(false);
  const [activityModal, setActivityModal] = useState(null);
  const navigate = useNavigate();
const handleSlideClick = (slide) => {
  if (!slide.target) return;

  if (slide.target.type === "route") {
    if (slide.target.path.startsWith("#")) {
      // scroll to section on the same page
      const el = document.querySelector(slide.target.path);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(slide.target.path); // navigate to other page
    }
  }

  if (slide.target.type === "external") {
    window.open(slide.target.url, "_blank");
  }
};


  return (
    <div className="bg-rose-50 text-gray-800">

      {/* ================= HERO SECTION ================= */}
      <section 
      id="home"
      className="relative w-full h-screen bg-rose-50">

        {/* 🔹 BACKGROUND LAYER (CLIPPED SAFELY) */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">

          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-pink-50 to-rose-200" />

          {/* Mask to fade waves before image */}
          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-rose-50 z-10" />

          {/* Waves */}
          <div
            className="absolute -left-1/2 top-[55%] w-[220%] h-[320px]
      bg-[radial-gradient(ellipse_at_center,rgba(255,105,180,0.45),transparent_70%)]
      animate-waveSlow"
          />
          <div
            className="absolute -left-1/2 top-[65%] w-[220%] h-[280px]
      bg-[radial-gradient(ellipse_at_center,rgba(236,72,153,0.35),transparent_72%)]
      animate-waveMedium"
          />
          <div
            className="absolute -left-1/2 top-[75%] w-[220%] h-[240px]
      bg-[radial-gradient(ellipse_at_center,rgba(219,39,119,0.25),transparent_75%)]
      animate-waveFast"
          />

          {/* Aura glows */}
          <div
            className="absolute bottom-[10%] left-[5%] w-[420px] h-[420px]
      bg-pink-400/35 rounded-full blur-[160px]
      animate-slowFloat"
          />
          <div
            className="absolute bottom-[5%] right-[5%] w-[480px] h-[480px]
      bg-rose-500/30 rounded-full blur-[180px]
      animate-slowFloatReverse"
          />
        </div>

        {/* 🔹 HERO SLIDER (UNTOUCHED BY WAVES) */}
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          autoplay={{ delay: 3500, disableOnInteraction: false }}
          loop
          pagination={{ clickable: true }}
          className="relative z-20 w-full h-full"
        >
          {heroSlides.map((slide, index) => (
  <SwiperSlide key={index} className="w-full h-full">

    <div
      onClick={() => handleSlideClick(slide)}
      className={`w-full h-full
        ${slide.target ? "cursor-pointer" : ""}
      `}
    >

      {/* Desktop */}
      <img
        src={slide.desktop}
        className="hidden md:block w-full h-full object-cover"
        alt="ISKCON Jaipur"
      />

      {/* Mobile */}
      <img
        src={slide.mobile}
        className="block md:hidden w-full h-full object-cover"
        alt="ISKCON Jaipur"
      />

    </div>

  </SwiperSlide>
))}

        </Swiper>



      </section>

      {/* ABOUT */}
      <section
        id="about"
        className="relative overflow-hidden bg-gradient-to-b from-[#FFF7FA] via-[#FFF1F8] to-[#FFEFF5] py-28"
      >
        {/* ABSTRACT SOFT SHAPES */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-pink-300 rounded-full opacity-30 blur-3xl animate-slowFloat"></div>
        <div className="absolute -bottom-24 -right-16 w-72 h-72 bg-purple-300 rounded-full opacity-25 blur-2xl animate-slowFloatReverse"></div>
        <div className="absolute top-10 right-10 w-48 h-48 bg-yellow-200 rounded-full opacity-20 blur-2xl animate-slowFloat"></div>

        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-12 relative z-10">
          {/* IMAGE */}
          <div className="relative w-64 h-64 md:w-72 md:h-72 flex-shrink-0">
            {/* GLOWING HALO */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#9A134C] via-pink-500 to-purple-500 blur-3xl animate-divineGlow opacity-40"></div>

            {/* MAIN IMAGE */}
            <img
              src="/images/about-image.webp"
              alt="About ISKCON Jaipur"
              className="relative w-full h-full object-cover rounded-full border-4 border-white shadow-2xl"
            />

            {/* FLOATING SPARKLES */}
            <div className="absolute inset-0">
              <span className="absolute w-2 h-2 bg-white rounded-full opacity-70 animate-slowFloat top-10 left-12"></span>
              <span className="absolute w-1.5 h-1.5 bg-white rounded-full opacity-60 animate-slowFloatReverse top-40 left-48"></span>
              <span className="absolute w-2.5 h-2.5 bg-white rounded-full opacity-50 animate-slowFloat top-60 left-20"></span>
            </div>
          </div>

          {/* TEXT */}
          <div className="flex-1 text-center md:text-left space-y-6">
            {/* HEADING */}
            <h2
              className="text-5xl pb-5 md:text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#9A134C] via-[#F472B6] to-[#C084FC] drop-shadow-xl"
              style={{ fontFamily: "'Great Vibes', 'Playfair Display', serif" }}
            >
              <span className="text-[#9A134C]">ISKCON Jaipur</span>
            </h2>

            {/* MAIN CONTENT */}
            <p className="text-gray-800 text-base md:text-lg leading-relaxed tracking-wide">
              Srila Prabhupada envisioned spreading the <span className="font-semibold text-[#9A134C]">worship of Sri Sri Radha Govindaji</span> to the whole world, expressing his desire to establish a <span className="font-semibold text-[#C084FC]">beautiful Krishna Balarama Temple in Jaipur</span>. In a letter, he wrote:
            </p>

            <blockquote className="text-gray-900 italic text-base md:text-lg leading-relaxed border-l-4 border-[#9A134C] pl-4">
              "So I want to open a <span className="font-semibold text-[#F472B6]">duplicate Krishna Balarama temple in Jaipur</span>. I am sending separately copies of pictures of our Krishna Balarama Temple in Vrindaban. Of course there is also Radha Krishna and Gaura Nitai. I wish that you have some land in Jaipur and help us in constructing a temple like in Vrindaban."
              <span className="block mt-2 font-semibold text-gray-700">– 13 July 1975</span>
            </blockquote>

            <p className="text-gray-800 text-base md:text-lg leading-relaxed tracking-wide">
              In <span className="font-semibold text-[#9A134C]">1991</span>, ISKCON Jaipur began its journey, inspired by Srila Prabhupada's vision. Then, in <span className="font-semibold text-[#C084FC]">2019</span>, the grand opening of the temple was held, fulfilling Srila Prabhupada's desire for a Krishna Balarama Mandir in Jaipur. Today, ISKCON Jaipur stands as a testament to his devotion and serves as a <span className="font-semibold text-[#F472B6]">beacon of spiritual enlightenment</span> for all.
            </p>
          </div>
        </div>
      </section>


      {/*SEVA OPPORTUNITY */}
      <section
        id="seva"
        className="relative py-10 bg-gradient-to-b from-[#FFF7FA] via-[#FFF1F8] to-[#FFEFF5] overflow-hidden"
      >

        {/* Waves */}
        <div
          className="absolute -left-1/2 top-[55%] w-[220%] h-[320px]
      bg-[radial-gradient(ellipse_at_center,rgba(255,105,180,0.45),transparent_70%)]
      animate-waveSlow"
        />
        <div
          className="absolute -left-1/2 top-[65%] w-[220%] h-[280px]
      bg-[radial-gradient(ellipse_at_center,rgba(236,72,153,0.35),transparent_72%)]
      animate-waveMedium"
        />
        <div
          className="absolute -left-1/2 top-[75%] w-[220%] h-[240px]
      bg-[radial-gradient(ellipse_at_center,rgba(219,39,119,0.25),transparent_75%)]
      animate-waveFast"
        />

        {/* Aura glows */}
        <div
          className="absolute bottom-[10%] left-[5%] w-[420px] h-[420px]
      bg-pink-400/35 rounded-full blur-[160px]
      animate-slowFloat"
        />
        <div
          className="absolute bottom-[5%] right-[5%] w-[480px] h-[480px]
      bg-rose-500/30 rounded-full blur-[180px]
      animate-slowFloatReverse"
        />

        {/* Section Heading */}
        <div className="text-center mb-12">
          <h2
            className="text-5xl pb-5 md:text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#9A134C] via-[#F472B6] to-[#C084FC] drop-shadow-xl"
            style={{ fontFamily: "'Great Vibes', 'Playfair Display', serif" }}
          >
            <span className="text-[#9A134C]">Seva Opportunities</span>
          </h2>
          <p className="mt-4 text-gray-700 md:text-lg max-w-2xl mx-auto">
            Explore our key service opportunities. Each seva contributes to the spiritual upliftment and smooth functioning of ISKCON Jaipur.
          </p>
        </div>

        {/* HORIZONTAL SCROLL CARDS */}
        <div className="relative px-6">
          <div className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory">
            {[
              {
                title: "Gau Seva",
                img: "/images/seva/gau-seva.jpeg",
                desc: "Care for cows and support the Gau Seva program",
                detail: "Join our Gau Seva program to nurture cows, support their shelter and well-being.",
              },
              {
                title: "Mandir Nirman Seva",
                img: "/images/seva/mandir-nirman.webp",
                desc: "Assist in temple construction and maintenance",
                detail: "Participate in Mandir Nirman Seva to contribute to temple construction and maintenance.",
              },
              {
                title: "Vigraha Seva",
                img: "/images/seva/vigraha.jpeg",
                desc: "Help maintain and beautify deities",
                detail: "Vigraha Seva allows devotees to serve and care for the divine idols in the temple.",
              },
              {
                title: "Nitya Seva",
                img: "/images/seva/nitya.jpeg",
                desc: "Daily temple service for smooth operations",
                detail: "Contribute in Nitya Seva by helping with daily rituals, decorations, and prayers.",
              },
              {
                title: "Annadan",
                img: "/images/seva/annadan.webp",
                desc: "Offer food to devotees and community",
                detail: "Participate in Annadan Seva to feed devotees and distribute prasadam.",
              },
              {
                title: "Festival Seva",
                img: "/images/seva/festival.jpeg",
                desc: "Assist in organizing grand festivals",
                detail: "Join Festival Seva to organize and celebrate spiritual festivals with grandeur.",
              },
              {
                title: "Sadhu Seva",
                img: "/images/seva/sadhu.jpeg",
                desc: "Support visiting saints and preachers",
                detail: "Engage in Sadhu Seva by welcoming and assisting visiting sadhus and guests.",
              },
              {
                title: "Ekadashi Seva",
                img: "/images/seva/ekadashi.jpeg",
                desc: "Help run Ekadashi fasting & events",
                detail: "Help in Ekadashi Seva by supporting fasting days and spiritual events in the temple.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="snap-start flex-shrink-0 w-64 md:w-72 h-96 bg-white/60 backdrop-blur-lg rounded-2xl border border-transparent hover:border-[#E7C873] hover:shadow-[0_10px_40px_rgba(231,200,115,0.5)] transition-all duration-300 relative cursor-pointer group"
                onClick={() => setModalData(card)}
              >
                {/* CARD IMAGE */}
                <div className="h-48 w-full overflow-hidden rounded-t-2xl">
                  <img
                    src={card.img}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                {/* CARD CONTENT */}
                <div className="p-4 flex flex-col justify-between h-48">
                  <h3 className="text-xl font-semibold text-[#9A134C]">{card.title}</h3>
                  <p className="text-gray-700 text-sm mt-2">{card.desc}</p>

                  {/* KNOW MORE BUTTON */}
                  <span className="mt-auto inline-block mt-4 text-[#E7C873] font-semibold border-b-2 border-transparent group-hover:border-[#E7C873] transition-all">
                    Know More →
                  </span>
                </div>

                {/* GOLD GLOW ON HOVER */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-[#E7C873] pointer-events-none"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Optional: subtle background floating lights */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 15 }).map((_, i) => (
            <span
              key={i}
              className={`absolute bg-yellow-200 rounded-full opacity-${Math.floor(Math.random() * 30 + 20)} w-${Math.floor(
                Math.random() * 3 + 1
              )} h-${Math.floor(Math.random() * 3 + 1)} animate-float`}
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 8}s`,
              }}
            ></span>
          ))}
        </div>

        {/* MODAL */}
        {modalData && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setModalData(null)}
          >
            <div
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl max-w-lg w-full relative p-6 scale-0 animate-scaleUp overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-xl font-bold"
                onClick={() => setModalData(null)}
              >
                ✕
              </button>

              {/* Image */}
              <img
                src={modalData.img}
                alt={modalData.title}
                className="w-full h-56 object-cover rounded-2xl mb-4"
              />

              <h3 className="text-2xl font-bold text-[#9A134C] mb-3">
                {modalData.title}
              </h3>

              {/* Dynamic Descriptive Content */}
              <div className="text-gray-800 space-y-5 text-[15px] leading-relaxed">

                {/* ================= GAU SEVA ================= */}
                {modalData.title === "Gau Seva" && (
                  <>
                    <p className="text-center italic text-[#9A134C] font-semibold">
                      नमो ब्रह्मण्य देवाय गोब्राह्मण हिताय च।
                      जगत् हिताय कृष्णाय गोविंदाय नमो नमः
                    </p>

                    <p>
                      This sacred shloka reminds us of <span className="font-semibold text-[#9A134C]">Lord Krishna’s divine love for cows</span>.
                      Offering service to Gau Mata is regarded as a direct path to receiving
                      <span className="font-semibold text-[#9A134C]">Krishna’s blessings</span>.
                    </p>

                    <p>
                      We are deeply committed to this service, currently caring for
                      <span className="font-bold text-[#9A134C]">20,000+ sick and injured cows</span>.
                      Our mission provides <span className="font-semibold">food, shelter, and medical care</span>,
                      rescuing cows affected by <span className="font-semibold">road accidents and illness</span>.
                    </p>

                    <p>
                      Though we operate one of India’s
                      <span className="font-semibold text-[#9A134C]">largest cow rehabilitation centers</span>,
                      the rising number of cows brings growing challenges.
                      We humbly seek your support to continue serving Gau Mata.
                    </p>

                    <p>
                      Your seva brings love and dignity to these
                      <span className="font-semibold text-[#9A134C]">roadside angels</span>,
                      ensuring they live with <span className="font-semibold">care, respect, and protection</span>.
                    </p>
                  </>
                )}

                {/* ================= VIGRAHA ================= */}
                {modalData.title === "Vigraha Seva" && (
                  <>
                    <p>
                      Step into the realm of <span className="font-semibold text-[#9A134C]">spiritual abundance</span>
                      by supporting the daily worship of the Deities.
                      Your donation becomes a channel for sacred offerings that invoke
                      <span className="font-semibold">prosperity, happiness, and spiritual growth</span>.
                    </p>

                    <p>
                      Worship includes numerous <span className="font-semibold">Aartis</span>,
                      opulent <span className="font-semibold">Bhog offerings</span>, and
                      beautiful <span className="font-semibold">decorations</span> that fill the temple with divine vibrations.
                    </p>

                    <p>
                      During festivals, your contribution ensures the
                      <span className="font-semibold text-[#9A134C]">magnificence of celebrations</span>,
                      glorifying the Divine Presence through
                      <span className="font-semibold">Kirtans, festivities, and prasadam distribution</span>.
                    </p>

                    <p>
                      Supporting this seva aligns you with the mercy of
                      <span className="font-semibold text-[#9A134C]">Sri Sri Giridhari Dauji</span>,
                      inviting blessings into every aspect of life.
                    </p>
                  </>
                )}

                {/* ================= NITYA ================= */}
                {modalData.title === "Nitya Seva" && (
                  <>
                    <p>
                      General donation supports ISKCON’s ongoing services —
                      <span className="font-semibold">temple worship</span>,
                      <span className="font-semibold">prasadam distribution</span>,
                      <span className="font-semibold">cow protection</span>, and
                      <span className="font-semibold">spiritual education</span>.
                    </p>

                    <p>
                      Every offering becomes an act of devotion, uplifting lives while serving
                      <span className="font-semibold text-[#9A134C]">Lord Krishna</span>.
                    </p>

                    <p>
                      Charity in Vedic wisdom is a
                      <span className="font-semibold">sacred spiritual practice</span>,
                      nurturing fellowship, compassion, and societal well-being.
                    </p>

                    <p>
                      Through thoughtful giving, devotees cultivate
                      <span className="font-semibold text-[#9A134C]">discernment, humility, and spiritual growth</span>.
                    </p>
                  </>
                )}

                {/* ================= MANDIR ================= */}
                {modalData.title === "Mandir Nirman Seva" && (
                  <>
                    <p>
                      Join us in building a
                      <span className="font-semibold text-[#9A134C]">Vedic Village</span>
                      dedicated to uplifting <span className="font-semibold">body, mind, and soul</span>.
                    </p>

                    <p>
                      This sacred space preserves
                      <span className="font-semibold">heritage, knowledge, and traditions</span>,
                      nurturing unity rooted in cultural richness.
                    </p>

                    <p>
                      Amid modern stress, the village will offer
                      <span className="font-semibold text-[#9A134C]">peace, rejuvenation, and spiritual refuge</span>.
                    </p>

                    <p>
                      Your support enables initiatives like
                      <span className="font-semibold">Annadaan</span> and
                      <span className="font-semibold">Goshala care</span>,
                      enriching the collective spirit of our community.
                    </p>
                  </>
                )}

                {/* ================= FESTIVAL ================= */}
                {modalData.title === "Festival Seva" && (
                  <>
                    <p>
                      Devotees often feel lost in modern life filled with pressure and distractions.
                      ISKCON festivals offer a
                      <span className="font-semibold text-[#9A134C]">sacred pause</span>
                      from this rush — inviting hearts to slow down and remember
                      <span className="font-semibold text-[#9A134C]">Shri Krishna with love</span>.
                    </p>

                    <p>
                      These celebrations blend
                      <span className="font-semibold">prayer, music, service, and joyful gathering</span>
                      into one uplifting spiritual experience.
                      Beyond enjoyment, they awaken
                      <span className="font-semibold text-[#9A134C]">
                        devotion, humility, and gratitude
                      </span>.
                    </p>

                    <p>
                      When devotees chant together and hear divine pastimes,
                      hearts soften naturally.
                      <span className="font-semibold text-[#9A134C]">
                        Shri Krishna becomes the center
                      </span>,
                      replacing material distractions with spiritual fulfillment.
                    </p>

                    <p>
                      The deeper purpose of these festivals is
                      <span className="font-semibold text-[#9A134C]">
                        spiritual awakening
                      </span>.
                      They remind devotees of their eternal relationship with the Supreme Lord
                      and teach surrender, compassion, and selfless service.
                    </p>

                    <p>
                      Festivals inspire joyful devotion —
                      not display or show — because Krishna accepts
                      <span className="font-semibold">simple, sincere love</span>.
                      Through chanting, scriptures, and unity, devotees cultivate
                      discipline, patience, and harmony.
                    </p>

                    <p>
                      Rooted in the path of
                      <span className="font-semibold text-[#9A134C]">Bhakti Yoga</span>,
                      each act of participation becomes an offering to Shri Krishna,
                      making ISKCON festivals vibrant, emotional, and spiritually fulfilling
                      for devotees of all ages.
                    </p>
                  </>
                )}


                {/* ================= ANNADAN ================= */}
                {modalData.title === "Annadan" && (
                  <>
                    <p className="italic text-[#9A134C] font-semibold">
                      “Annadan brings blessings and nourishes those in need.”
                    </p>

                    <p>
                      This seva ensures temple visitors receive
                      <span className="font-semibold">wholesome and sanctified meals</span>.
                    </p>

                    <p>
                      Lord Krishna teaches that
                      <span className="font-semibold text-[#9A134C]">all beings subsist on food grains</span>,
                      emphasizing food’s sacred role in sustaining life.
                    </p>

                    <p>
                      Distribution of prasadam nurtures both
                      <span className="font-semibold">body and soul</span>,
                      fostering connection, compassion, and community harmony.
                    </p>
                  </>
                )}

                {/* ================= EKADASHI ================= */}
                {modalData.title === "Ekadashi Seva" && (
                  <>
                    <p>
                      Ekadashi is a sacred day beloved to
                      <span className="font-semibold text-[#9A134C]">Lord Vishnu and Krishna</span>.
                    </p>

                    <p>
                      Observing fasting, chanting, and charity purifies devotion and
                      <span className="font-semibold">strengthens spiritual discipline</span>.
                    </p>

                    <p>
                      Donations on this day invite
                      <span className="font-semibold text-[#9A134C]">divine mercy and blessings</span>,
                      softening the heart and deepening surrender.
                    </p>
                  </>
                )}

                {/* ================= SADHU ================= */}
                {modalData.title === "Sadhu Seva" && (
                  <>
                    <p>
                      Saints embody sacrifice, dedicating their lives to
                      <span className="font-semibold text-[#9A134C]">uplifting humanity</span>.
                    </p>

                    <p>
                      Living by the spirit of
                      <span className="font-semibold">Vasudev Kutumbakam</span>,
                      they spread scriptural wisdom for society’s harmony.
                    </p>

                    <p>
                      Serving saints supports the path of
                      <span className="font-semibold text-[#9A134C]">Dharma, peace, and prosperity</span>,
                      making this seva deeply dear to Krishna.
                    </p>
                  </>
                )}

              </div>


              {/* Donate Button */}
              <Link
                to="/donate"
                onClick={() => window.scrollTo(0,0)}
                className="mt-6 inline-block w-full py-3 text-center bg-[#9A134C] text-white rounded-xl font-semibold hover:bg-[#7A0F39] transition-all duration-300"
              >
                Donate
              </Link>
            </div>
          </div>
        )}


        <style>
          {`
      @keyframes float {
        0% { transform: translateY(0px) translateX(0px) scale(1);}
        50% { transform: translateY(-10px) translateX(5px) scale(1.1);}
        100% { transform: translateY(0px) translateX(0px) scale(1);}
      }
      .animate-float {
        animation-name: float;
        animation-timing-function: ease-in-out;
        animation-iteration-count: infinite;
      }

      @keyframes scaleUp {
        0% { transform: scale(0.8); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      .animate-scaleUp {
        animation: scaleUp 0.3s ease-out forwards;
      }
      .scrollbar-hide::-webkit-scrollbar { display: none; }
      .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    `}
        </style>
      </section>

      {/* DONATION IMPACT - COLLAGE STYLE */}

      <section 
      className="relative py-24 bg-rose-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          {/* Heading */}
          <h2 className="text-5xl font-extrabold text-[#9A134C] mb-6 tracking-tight" style={{ fontFamily: "'Cinzel', serif" }}>
            Your Contribution Creates Impact
          </h2>
          <p className="text-gray-700 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
            Every donation helps in spiritual education, prasadam distribution, and empowering devotees worldwide.
          </p>

          {/* Collage Image */}
          <img
            src="/images/impact/collage.png"
            alt="Donation Impact Collage"
            className="w-full max-w-6xl mx-auto rounded-3xl shadow-2xl object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
            onClick={() => setModalOpen(true)}
          />

          {/* Buttons Wrapper */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">

            {/* Learn More Button */}
            <button
              onClick={() => setModalOpen(true)}
              className="bg-gradient-to-r from-[#9A134C] to-[#E7C873] text-white px-14 py-4 rounded-2xl text-lg font-semibold shadow-xl hover:shadow-rose-300/30 hover:scale-105 transition duration-300"
            >
              Learn More
            </button>

            {/* Our Donors */}
            <a
              href="/donate#donors"
              className="border-2 border-[#9A134C] text-[#9A134C] px-14 py-4 rounded-2xl text-lg font-semibold hover:bg-[#9A134C] hover:text-white transition duration-300 text-center"
            >
              Our Donors
            </a>

          </div>
        </div>

        {/* Modal */}
        {modalOpen && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setModalOpen(false)}
          >
            <div
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full relative scale-0 animate-scaleUp flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-2xl font-bold z-10"
                onClick={() => setModalOpen(false)}
              >
                ✕
              </button>

              {/* Same Image as Section */}
              <img
                src="/images/impact/collage.png"
                alt="Donation Impact"
                className="w-full h-64 md:h-80 object-cover rounded-t-3xl"
              />

              {/* Scrollable Content */}
              <div className="p-6 overflow-y-auto">
                <h3 className="text-3xl font-bold text-[#9A134C] mb-4">
                  The Living Impact of Your Contributions
                </h3>

                <p className="text-gray-800 leading-relaxed space-y-4">
                  Your support is not just a donation — it becomes an offering that
                  nourishes lives, preserves sacred traditions, and spreads spiritual
                  wisdom. Every contribution directly empowers meaningful initiatives
                  that serve both society and devotion.
                  <br /><br />

                  <strong>Cow Protection & Adoption</strong><br />
                  Donations help care for protected cows by providing shelter, medical
                  care, and nourishing feed. Supporting cow seva preserves an essential
                  pillar of Vedic culture and compassion toward all living beings.
                  <br /><br />

                  <strong>Sunday Khichdi Distribution</strong><br />
                  Every Sunday, sanctified khichdi is lovingly prepared and distributed
                  to everyone who comes. This prasadam nourishes both body and soul,
                  ensuring that no visitor leaves without receiving Krishna’s mercy.
                  <br /><br />

                  <strong>Lunch Prasadam Distribution</strong><br />
                  Daily prasadam distribution allows countless individuals to receive
                  sanctified meals. These offerings foster community bonding, gratitude,
                  and spiritual connection through sacred food.
                  <br /><br />

                  <strong>Sadhu Feasts & Seva</strong><br />
                  Contributions support the respectful hosting of sadhus and visiting
                  devotees. Through meals, service, and hospitality, we honor saintly
                  association and uphold timeless spiritual traditions.
                  <br /><br />

                  <strong>Temple Maintenance</strong><br />
                  Donations sustain the upkeep of the temple — preserving a sacred space
                  of worship, meditation, and community gathering for thousands of
                  visitors seeking peace and guidance.
                  <br /><br />

                  <strong>Grand Spiritual Festivals</strong><br />
                  From Janmashtami to Gaura Purnima and many other celebrations, your
                  support enables large-scale festivals filled with kirtan, drama,
                  decoration, and prasadam distribution that inspire devotion in
                  countless hearts.
                  <br /><br />

                  <strong>Bhagavad-gita Distribution</strong><br />
                  Sharing spiritual literature spreads wisdom beyond temple walls.
                  Contributions allow sacred texts to reach new seekers, transforming
                  lives through timeless teachings.
                  <br /><br />

                  Through every initiative, your generosity becomes a channel for
                  service, compassion, and spiritual upliftment. Together, we create a
                  living impact that continues to benefit devotees, visitors, and the
                  wider community.
                </p>
              </div>
            </div>
          </div>
        )}


        <style>
          {`
      @keyframes scaleUp {
        0% { transform: scale(0); opacity: 0; }
        50% { transform: scale(1.05); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      .animate-scaleUp { animation: scaleUp 0.3s ease-out forwards; }
    `}
        </style>
      </section>


      {/* ACTIVITIES SECTION */}
      <section id="activities" className="relative py-10 bg-rose-50 overflow-hidden bg-gradient-to-b from-rose-200 via-pink-100 to-rose-300
">
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          {/* Heading */}
          <h2
            className="text-5xl font-extrabold text-[#9A134C] mb-6 tracking-tight"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Our Activities
          </h2>
          <p className="text-gray-700 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
            Explore the various programs and spiritual activities we conduct at ISKCON Jaipur for all age groups.
          </p>

          {/* Desktop Grid */}
          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-3 gap-8">
            {[
              {
                title: "Online Classes",
                image: "/images/activities/class1.jpeg",
                desc: "Understanding Gita in a practical, life-transforming way for spiritual growth.",
                modalDesc:
                  "We conduct daily online spiritual sessions on Zoom in both morning and evening. These include study of Bhagavad-gita, Srimad Bhagavatam, Chaitanya Charitamrita, Vaishnava etiquette, girls sessions, and more. Participants receive personal mentoring, open discussions, and doubt-clearing guidance that supports practical spiritual growth.",
              },
              {
                title: "Bhakti Vriksha",
                image: "/images/activities/bhaktivriksha.jpeg",
                desc: "Small spiritual groups fostering care, connection, and devotion among devotees.",
                modalDesc:
                  "Bhakti Vriksha is a congregational temple gathering in small groups of about 30 devotees. Sessions include katha, kirtan, discussions on Bhagavad-gita principles, and nourishing association. Each gathering concludes with joyful bonding and delicious prasadam, strengthening both devotion and community.",
              },
              {
                title: "Jagriti",
                image: "/images/activities/jagriti.jpeg",
                desc: "Youth-focused spiritual gatherings empowering women leaders.",
                modalDesc:
                  "Jagriti is a mega youth girls festival organized annually at the temple featuring dance, drama, debate, ramp walk, panel talks, kirtan and more. Alongside this, smaller themed events like Vrindavan Vibes and other programs inspire creativity, confidence and devotional connection.",
              },
              {
                title: "Mentorship",
                image: "/images/activities/mentorship.jpeg",
                desc: "Personal spiritual and emotional guidance.",
                modalDesc:
                  "Participants are assigned personal mentors who guide them in practical life and spiritual development. Through group and individual interaction, mentors provide encouragement, clarity, and wisdom for decision-making, emotional growth and devotional practice.",
              },
              {
                title: "Kirtan Evenings",
                image: "/images/activities/kirtan.jpeg",
                desc: "Soulful kirtans uplifting devotion.",
                modalDesc:
                  "Daily kirtan before the beautiful Deities fills the temple with joy and devotion. Devotees chant together, dance, and experience spiritual happiness in a vibrant atmosphere of unity and divine remembrance.",
              },
              {
                title: "Youth Programs",
                image: "/images/activities/youth.jpg",
                desc: "Programs designed for young devotees.",
                modalDesc:
                  "Youth initiatives provide guidance on life values, career direction, and spiritual principles while encouraging association with like-minded peers. These programs empower youth to lead purposeful and balanced lives rooted in dharmic wisdom.",
              },
            ].map((activity, i) => (

              <div
                onClick={() => setActivityModal(activity)}
                key={i}
                className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden flex flex-col"
              >
                {/* Image */}
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-2xl font-semibold text-[#9A134C] mb-2">{activity.title}</h3>
                  <p className="text-gray-700 text-sm md:text-base mb-4 line-clamp-2">{activity.desc}</p>
                  <button
                    onClick={() => setActivityModal(activity)}
                    className="mt-auto inline-block text-[#E7C873] font-semibold border-b-2 border-transparent hover:border-[#E7C873] transition-all text-left"
                  >
                    View Details →
                  </button>

                </div>
              </div>
            ))}
          </div>

          {/* Mobile Sliding Cards with Swipe Dots */}
          <div className="md:hidden px-2">
            <Swiper
              modules={[Pagination, Autoplay]}
              slidesPerView={1}
              spaceBetween={16}
              loop={true}
              pagination={{ clickable: true }} // ← dots below
              autoplay={{ delay: 3500, disableOnInteraction: false }}
              className="!overflow-visible"
            >
              {[
                {
                  title: "Online Classes",
                  image: "/images/activities/class1.jpeg",
                  desc: "Understanding Gita in a practical, life-transforming way for spiritual growth.",
                  modalDesc:
                    "We conduct daily online spiritual sessions on Zoom in both morning and evening. These include study of Bhagavad-gita, Srimad Bhagavatam, Chaitanya Charitamrita, Vaishnava etiquette, girls sessions, and more. Participants receive personal mentoring, open discussions, and doubt-clearing guidance that supports practical spiritual growth.",
                },
                {
                  title: "Bhakti Vriksha",
                  image: "/images/activities/bhaktivriksha.jpeg",
                  desc: "Small spiritual groups fostering care, connection, and devotion among devotees.",
                  modalDesc:
                    "Bhakti Vriksha is a congregational temple gathering in small groups of about 30 devotees. Sessions include katha, kirtan, discussions on Bhagavad-gita principles, and nourishing association. Each gathering concludes with joyful bonding and delicious prasadam, strengthening both devotion and community.",
                },
                {
                  title: "Jagriti",
                  image: "/images/activities/jagriti.jpeg",
                  desc: "Youth-focused spiritual gatherings empowering women leaders.",
                  modalDesc:
                    "Jagriti is a mega youth girls festival organized annually at the temple featuring dance, drama, debate, ramp walk, panel talks, kirtan and more. Alongside this, smaller themed events like Vrindavan Vibes and other programs inspire creativity, confidence and devotional connection.",
                },
                {
                  title: "Mentorship",
                  image: "/images/activities/mentorship.jpeg",
                  desc: "Personal spiritual and emotional guidance.",
                  modalDesc:
                    "Participants are assigned personal mentors who guide them in practical life and spiritual development. Through group and individual interaction, mentors provide encouragement, clarity, and wisdom for decision-making, emotional growth and devotional practice.",
                },
                {
                  title: "Kirtan Evenings",
                  image: "/images/activities/kirtan.jpeg",
                  desc: "Soulful kirtans uplifting devotion.",
                  modalDesc:
                    "Daily kirtan before the beautiful Deities fills the temple with joy and devotion. Devotees chant together, dance, and experience spiritual happiness in a vibrant atmosphere of unity and divine remembrance.",
                },
                {
                  title: "Youth Programs",
                  image: "/images/activities/youth.jpg",
                  desc: "Programs designed for young devotees.",
                  modalDesc:
                    "Youth initiatives provide guidance on life values, career direction, and spiritual principles while encouraging association with like-minded peers. These programs empower youth to lead purposeful and balanced lives rooted in dharmic wisdom.",
                },
              ].map((activity, i) => (
                <SwiperSlide key={i}>
                  <div
                    onClick={() => setActivityModal(activity)}
                    className="flex-shrink-0 w-72 bg-white rounded-3xl shadow-lg overflow-hidden transition-shadow duration-300 hover:shadow-2xl cursor-pointer mx-auto">
                    <div className="h-44 w-full overflow-hidden">
                      <img
                        src={activity.image}
                        alt={activity.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                    <div className="p-4 flex flex-col h-40">
                      <h3 className="text-xl font-semibold text-[#9A134C]">{activity.title}</h3>
                      <p className="text-gray-700 text-sm mt-1 line-clamp-2">{activity.desc}</p>
                      <button
                        onClick={() => setActivityModal(activity)}
                        className="mt-auto inline-block text-[#E7C873] font-semibold border-b-2 border-transparent hover:border-[#E7C873] transition-all"
                      >
                        View Details →
                      </button>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

        </div>

        {activityModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setActivityModal(null)}
          >
            <div
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full flex flex-col max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-xl font-bold"
                onClick={() => setActivityModal(null)}
              >
                ✕
              </button>

              <img
                src={activityModal.image}
                alt={activityModal.title}
                className="w-full h-56 object-cover rounded-t-3xl"
              />

              {/* SCROLLABLE BODY */}
              <div className="p-6 overflow-y-auto">
                <h3 className="text-2xl font-bold text-[#9A134C] mb-3">
                  {activityModal.title}
                </h3>

                <p className="text-gray-800 leading-relaxed">
                  {activityModal.modalDesc}
                </p>
              </div>

            </div>
          </div>
        )}


        <style>{`
    /* Hide scrollbar for mobile sliding cards */
    .scrollbar-hide::-webkit-scrollbar { display: none; }
    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
  `}</style>
      </section>


      {/* FOUNDER SPOTLIGHT */}
      <section 
      id="prabhupada"
      className="relative bg-gradient-to-br from-rose-900 via-rose-950 to-rose-900 text-white py-24 overflow-hidden">
        {/* Floating Glows */}
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-pink-600/20 rounded-full blur-3xl animate-slowFloat"></div>
        <div className="absolute -bottom-20 -right-16 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl animate-slowFloatReverse"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2 animate-slowFloat"></div>

        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center gap-12 relative z-10">

          {/* Founder Image */}
          <div className="relative flex-shrink-0 w-52 h-52 md:w-64 md:h-64 rounded-full overflow-hidden border-4 border-rose-700 shadow-2xl">
            {/* Halo Glow */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 opacity-30 blur-xl animate-divineGlow"></div>
            <img
              src="/images/prabhupada.jpg"
              alt="Founder of ISKCON"
              className="relative w-full h-full object-cover scale-105"
            />
            {/* Floating Sparkles */}
            <span className="absolute top-10 left-12 w-2 h-2 bg-white rounded-full opacity-70 animate-slowFloat"></span>
            <span className="absolute bottom-16 right-16 w-1.5 h-1.5 bg-white rounded-full opacity-60 animate-slowFloatReverse"></span>
          </div>

          {/* Founder Info */}
          <div className="text-center md:text-left max-w-xl space-y-4">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-wide drop-shadow-[0_0_18px_rgba(255,255,255,0.7)]">
              Founder Acharya
            </h2>
            <p className="text-gray-200 text-lg leading-relaxed">
              His Divine Grace A.C. Bhaktivedanta Swami Prabhupada founded ISKCON in 1966
              to spread the teachings of the Bhagavad Gita and Krishna consciousness worldwide.
              Through his vision, millions have embraced devotional service and spiritual education.
            </p>
            <button
              className="mt-4 inline-block bg-[#E7C873] text-[#9A134C] font-semibold px-8 py-3 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
              onClick={() => setFounderModal(true)}
            >
              Read More
            </button>
          </div>

        </div>

        {/* Optional Founder Modal */}
        {founderModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setFounderModal(false)}
          >
            <div
              className="relative bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-scaleUp"
              onClick={(e) => e.stopPropagation()}
            >

              {/* Top Gradient Banner */}
              <div className="h-3 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500" />

              {/* Close Button */}
              <button
                className="absolute top-4 right-5 text-gray-500 hover:text-black text-2xl font-bold"
                onClick={() => setFounderModal(false)}
              >
                ✕
              </button>

              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[90vh] p-8 space-y-6">

                {/* Image */}
                <img
                  src="/images/prabhupada2.jpg"
                  alt="Srila Prabhupada"
                  className="w-full h-72 object-cover rounded-2xl shadow-lg"
                />

                {/* Title */}
                <h3 className="text-3xl font-bold text-[#9A134C] text-center">
                  His Divine Grace A.C. Bhaktivedanta Swami Prabhupada
                </h3>

                {/* Text Content */}
                <div className="space-y-4 text-gray-700 leading-relaxed text-justify">

                  <p>
                    His Divine Grace A. C. Bhaktivedanta Swami Prabhupada,
                    Founder-Acharya of International Society for Krishna Consciousness
                    (ISKCON), was born Abhay Charan De, on 1 September 1896, in Calcutta.
                    In 1922 he met His Divine Grace Bhaktisiddanta Sarasvati Thakur,
                    Founder of Gaudiya Math, who requested Abhay to broadcast Vedic
                    knowledge in English.
                  </p>

                  <p>
                    In 1933, at Allahabad, Abhay was formally initiated and made it his
                    life ambition to expound the Vedic conclusion that real freedom means
                    liberation from the miseries of material life — birth, death, old age,
                    and disease — a state attained by awakening pure love for God,
                    Krishna-prema.
                  </p>

                  <p>
                    In later years he wrote a commentary on the Bhagavad-gita and in 1944
                    started the Back to Godhead magazine, which continues today through
                    his disciples.
                  </p>

                  <p>
                    Honored with the title Bhaktivedanta in 1947, he retired from married
                    life and lived in Vrindavan at the Radha Damodar temple. In 1959 he
                    accepted sannyasa and began translating the multi-volume
                    Srimad Bhagavatam.
                  </p>

                  <p>
                    In 1965, at age 69, he traveled to the United States to fulfill his
                    guru’s mission and founded ISKCON, bringing the teachings of Lord
                    Caitanya Mahaprabhu and the chanting of the Hare Krishna mantra to
                    the West.
                  </p>

                  <p>
                    Srila Prabhupada taught on a universal level that every living being
                    is an eternal servant of Lord Krishna with a natural propensity to
                    experience the bliss of divine love of God.
                  </p>

                </div>

              </div>
            </div>
          </div>
        )}

      </section>

    </div>
  );
};


export default Home;

